import CollectAreCurrentMedicationsHelping from '@components/app/TrialApply/CollectAreCurrentMedicationsHelping.client'
import CollectAreCurrentMedicationsStableDose from '@components/app/TrialApply/CollectAreCurrentMedicationsStableDose.client'
import CollectAreCurrentMedicationsWillingToStop from '@components/app/TrialApply/CollectAreCurrentMedicationsWillingToStop.client'
import CollectAreCurrentSubstancesWillingToStop from '@components/app/TrialApply/CollectAreCurrentSubstancesWillingToStop.client'
import CollectComorbidities from '@components/app/TrialApply/CollectComorbidities.client'
import CollectCurrentMedications from '@components/app/TrialApply/CollectCurrentMedications.client'
import CollectCurrentSubstanceFrequency from '@components/app/TrialApply/CollectCurrentSubstanceFrequency.client'
import CollectCurrentSubstances from '@components/app/TrialApply/CollectCurrentSubstances.client'
import CollectStage from '@components/app/TrialApply/CollectStage.client'
import { TherapeuticArea } from '@lib/types/TherapeuticArea'
import { type PatientApplyProfileData } from '@modules/patientApplyProfile/helpers/PatientApplyProfileData'
import { shouldHideQuestionFromPatient } from './shouldHideQuestionFromPatient'
import type { ConditionsSurveyQuestion } from './types'

export type ConditionAttributesType = keyof typeof ConditionAttributes

export const ConditionAttributes = {
  areCurrentMedicationsHelping: 'areCurrentMedicationsHelping' as const,
  areCurrentMedicationsStableDose: 'areCurrentMedicationsStableDose' as const,
  areCurrentMedicationsWillingToStop:
    'areCurrentMedicationsWillingToStop' as const,
  areCurrentSubstancesWillingToStop:
    'areCurrentSubstancesWillingToStop' as const,
  comorbidities: 'comorbidities' as const,
  currentMedications: 'currentMedications' as const,
  currentSubstanceFrequency: 'currentSubstanceFrequency' as const,
  currentSubstances: 'currentSubstances' as const,
  currentTreatments: 'currentTreatments' as const,
  durationSinceDiagnosis: 'durationSinceDiagnosis' as const,
  geneticMarkers: 'geneticMarkers' as const,
  lineOfTherapy: 'lineOfTherapy' as const,
  priorTreatments: 'priorTreatments' as const,
  stageProgression: 'stageProgression' as const,
}

export const SurveyQuestionsConditions: ConditionsSurveyQuestion[] = [
  {
    category: 'conditions',
    component: CollectStage,
    id: ConditionAttributes.stageProgression,
    questionText: 'What stage of the disease are you in?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.stageProgression,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [TherapeuticArea.Oncology],
  },
  {
    category: 'conditions',
    component: CollectCurrentMedications,
    id: ConditionAttributes.currentMedications,
    questionText: 'What medications are you currently taking?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.currentMedications,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [
      TherapeuticArea.Dermatology,
      TherapeuticArea.Neurology,
      TherapeuticArea.Oncology,
      TherapeuticArea.Psychiatry,
    ],
  },
  {
    category: 'conditions',
    component: CollectAreCurrentMedicationsHelping,
    id: ConditionAttributes.areCurrentMedicationsHelping,
    questionText: 'Are your medications helping you?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.areCurrentMedicationsHelping,
        parentQuestionName: ConditionAttributes.currentMedications,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [
      TherapeuticArea.Dermatology,
      TherapeuticArea.Neurology,
      TherapeuticArea.Psychiatry,
    ],
  },
  {
    category: 'conditions',
    component: CollectAreCurrentMedicationsStableDose,
    id: ConditionAttributes.areCurrentMedicationsStableDose,
    questionText:
      'Have you been taking these medications for at least 1 month?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.areCurrentMedicationsStableDose,
        parentQuestionName: ConditionAttributes.currentMedications,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [
      TherapeuticArea.Dermatology,
      TherapeuticArea.Neurology,
      TherapeuticArea.Psychiatry,
    ],
  },
  {
    category: 'conditions',
    component: CollectAreCurrentMedicationsWillingToStop,
    id: ConditionAttributes.areCurrentMedicationsWillingToStop,
    questionText: 'Would you be open to stopping your medications if needed?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.areCurrentMedicationsWillingToStop,
        parentQuestionName: ConditionAttributes.currentMedications,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [
      TherapeuticArea.Dermatology,
      TherapeuticArea.Neurology,
      TherapeuticArea.Psychiatry,
    ],
  },
  {
    category: 'conditions',
    component: CollectComorbidities,
    id: ConditionAttributes.comorbidities,
    questionText:
      'What other conditions have you been diagnosed with other than your primary condition?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.comorbidities,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [
      TherapeuticArea.Dermatology,
      TherapeuticArea.Neurology,
      TherapeuticArea.Psychiatry,
    ],
  },
  {
    category: 'conditions',
    component: CollectCurrentSubstances,
    id: ConditionAttributes.currentSubstances,
    questionText:
      'Which of these substances have you used in the past 6-months?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.currentSubstances,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [TherapeuticArea.Neurology, TherapeuticArea.Psychiatry],
  },
  {
    category: 'conditions',
    component: CollectAreCurrentSubstancesWillingToStop,
    id: ConditionAttributes.areCurrentSubstancesWillingToStop,
    questionText:
      'Would you be willing to stop using these substances for the duration of the trial?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.areCurrentSubstancesWillingToStop,
        parentQuestionName: ConditionAttributes.currentSubstances,
        patientProfileData,
      })
    },
    skippable: true,
    therapeuticArea: [TherapeuticArea.Neurology, TherapeuticArea.Psychiatry],
  },
  {
    category: 'conditions',
    component: CollectCurrentSubstanceFrequency,
    id: ConditionAttributes.currentSubstanceFrequency,
    questionText: 'How often do you use these substances?',
    shouldShowQuestionToPatient: (
      patientProfileData: PatientApplyProfileData,
    ) => {
      return shouldHideQuestionFromPatient({
        attribute: ConditionAttributes.currentSubstanceFrequency,
        parentQuestionName:
          ConditionAttributes.areCurrentSubstancesWillingToStop,
        patientProfileData,
        validParentQuestionValues: ['no'],
      })
    },
    skippable: true,
    therapeuticArea: [TherapeuticArea.Neurology, TherapeuticArea.Psychiatry],
  },
]
